import {
  Component,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { Store } from '@ngrx/store';
import { ChatMessageThinking } from '@shared';
import * as fromGenerated from '../../../_generated';
import * as fromFeatureFlags from '../../../feature-flags';
import * as fromShared from '../../shared';
import {
  chatsFeature,
  ChatsState,
  SelectDocumentInChatMessagePayload,
} from '../../store';
import { DocumentInChatMessage } from '../conversation.page';
import { ReflexionBlockComponent } from '../reflexion/reflexion-block.component';
import {
  ImageInMessageClickedEvent,
  MessageDocuments,
  MessagesListingMessageComponent,
} from './message/message-listing-message.component';

/**
 * SB-3944 / KD-50: the message id of the intro bubble, which is no stored
 * message. Stored ids start at 1, so it never matches one.
 */
export const INTRO_MESSAGE_ID = 0;

export interface MessageListingType {
  id: number;
  content: string;
  documents?: (MessageDocuments &
    Partial<Pick<fromGenerated.DocumentView, 'role'>>)[];
  isFromUser: boolean;
  documentsChunks?: fromGenerated.DocumentChunkView[];
  thinking?: fromGenerated.ChatMessageView['thinking'];
}

/**
 * SB-3651 — a message's saved Réflexion trace, or `null` when it has none or
 * a payload version this client doesn't know (render nothing rather than
 * guess). A trace with no step is kept: it reads « Réfléchi pendant 5 s ». The
 * payload is untrusted model output rendered as text.
 */
export const savedThinking = (
  thinking: MessageListingType['thinking'],
): ChatMessageThinking | null => {
  const candidate = thinking as Partial<ChatMessageThinking> | null | undefined;
  return candidate?.v === 1 &&
    Array.isArray(candidate.steps) &&
    typeof candidate.durationMs === 'number'
    ? (candidate as ChatMessageThinking)
    : null;
};

/**
 * Drops instruction-role documents (the skill.md, SB-3325/KD-15) so they never
 * render as chips on a chat message. The assistant profile and chat side-panel
 * already filter these out via `toSources` in `chats.selectors.ts`; the answer
 * message binds `message.documents` directly, so it needs the same filter here —
 * before the child `[messageDocuments]` binding, since `MessageDocuments` carries
 * no `role`. Mirrors `isInstructionFile`'s intent for message docs, where
 * `role === 'instruction'` alone is sufficient (the filename fallback there is
 * scoped to assistant-owned docs, so genuine user uploads named skill.md — which
 * keep role `regular` — still render).
 */
export const excludeInstructionDocuments = <
  T extends Partial<Pick<fromGenerated.DocumentView, 'role'>>,
>(
  documents: readonly T[] | undefined,
): T[] =>
  (documents ?? []).filter((document) => document.role !== 'instruction');

@Component({
  selector: 'squadbox-messages-listing',
  imports: [MessagesListingMessageComponent, ReflexionBlockComponent],
  template: `
    <div
      class="flex flex-col gap-10"
      [class.pb-4]="!isInCompliancePhaseRegular()"
    >
      <!-- LOADING MESSAGES -->
      @if (messageCount(); as messageCount) {
        @if (isLoading()) {
          @for (
            message of [].constructor(messageCount);
            track $index;
            let i = $index
          ) {
            <squadbox-messages-listing-message
              [isFromUser]="i % 2 !== 0"
              [isLoading]="true"
              [messageId]="i"
              [messageContent]="loadingMessage()"
              [class]="{
                'self-end': i % 2 !== 0,
                'self-start': i % 2 === 0,
              }"
            />
          }
        } @else {
          <!-- SB-3944 / KD-50: a skill chat's intro is not a stored message;
               it is the skill's current intro, shown as the first bubble. -->
          @if (introMessage(); as intro) {
            <squadbox-messages-listing-message
              id="message-intro"
              [isFromUser]="false"
              [messageId]="introMessageId"
              [messageContent]="intro"
              [isAssistantDescriptionOrIntroMessage]="true"
              class="max-w-full"
              [isMobile]="isMobile()"
            />
          }
          @for (message of messagesWithLastMessageFromChatbot(); track $index) {
            <!-- SB-3651 — a reply held back while its live turn finishes the
                 simulation's sequence (fold, then stream) is not shown twice. -->
            @if (!(holdLastReply() && message.isLastMessageFromChatbot)) {
              <!-- A saved trace sits with its own reply (the simulation's
                   16px from the trace's divider to the answer), not a full
                   message gap above it. -->
              <div class="flex flex-col gap-1">
                @if (ffThinkingStreaming() && message.thinking; as thinking) {
                  <!-- SB-3651 — every assistant message's own saved trace,
                       collapsed, so it survives a reload, the next answer and
                       switching chats. startedAt 0 + answerStartedAt = the saved
                       duration is what the header and footer count. The live
                       trace was already folded when the answer started, so this
                       one mounts folded too — no jump at the handoff. -->
                  <squadbox-reflexion-block
                    [steps]="thinking.steps"
                    [startedAt]="0"
                    [answerStartedAt]="thinking.durationMs"
                    [summary]="
                      typeof thinking.summary === 'string'
                        ? thinking.summary
                        : null
                    "
                    [live]="false"
                    [inertLinks]="inertTraceLinks()"
                  />
                }
                <squadbox-messages-listing-message
                  [id]="'message-' + message.id"
                  [isFromUser]="message.isFromUser"
                  [chatUuid]="chatUuid()"
                  [chatId]="chatId()"
                  [messageId]="message.id"
                  [messageContent]="message.content"
                  [messageDocuments]="message.documents"
                  [isQuickActionsDisplayed]="!message.isFromUser"
                  [isLastResponse]="message.isLastMessageFromChatbot"
                  [isProcessing]="isThinking()"
                  [isNewReply]="
                    !ffThinkingStreaming() &&
                    !isThinking() &&
                    newReplyMessageId() === message.id
                  "
                  [wasStreamed]="
                    ffThinkingStreaming() &&
                    message.isLastMessageFromChatbot &&
                    replyShownLive()
                  "
                  class="max-w-full"
                  [isMobile]="isMobile()"
                  [selectedDocumentInChatMessage]="
                    selectedDocumentInChatMessage()
                  "
                  [isCompliancePhaseRegular]="isInCompliancePhaseRegular()"
                  (documentClicked)="
                    handleMessageDocumentClicked({
                      chatMessageId: message.id,
                      documentUuid: $event.documentUuid,
                    })
                  "
                  (imageClicked)="imageInChatMessageClicked.emit($event)"
                  (markdownAnimationComplete)="
                    handleMarkdownAnimationComplete(message.id)
                  "
                  (spacerShouldRecalculate)="
                    handleSpacerShouldRecalculate(message.id)
                  "
                />
              </div>
            }
          }
        }
      }
      <!-- SB-3651 — the live turn, projected by the page: the last item of
           this column, so it sits exactly where its saved reply will (the
           same gap-10) and nothing moves when that reply takes over. -->
      <ng-content />
    </div>
  `,
})
export class MessagesListingComponent {
  private readonly chatsStore = inject<Store<ChatsState>>(Store);
  public readonly chatUuid = this.chatsStore.selectSignal(
    chatsFeature.selectSelectedChatUuid,
  );
  // The numeric Chat.id feeds the copy and export analytics events only
  // (SB-3944 / KD-28).
  public readonly chatId = this.chatsStore.selectSignal(
    chatsFeature.selectSelectedChatNumericId,
  );
  /**
   * SB-3651 — off: the waiting UI as before streaming (the GSAP reveal of a
   * reply that landed, no saved trace). On: the live turn and every reply's
   * saved trace.
   */
  protected readonly ffThinkingStreaming = this.chatsStore.selectSignal(
    fromFeatureFlags.featureFlagsFeature.selectThinkingStreaming,
  );
  /**
   * SB-3651 — the newest reply was just shown by the live turn, which wrote it
   * out: its reply must not fade in again at the handoff.
   */
  protected readonly replyShownLive = this.chatsStore.selectSignal(
    chatsFeature.selectWasLastAnswerShownLive,
  );
  public readonly isInCompliancePhaseRegular = computed(
    () =>
      this.chatsStore.selectSignal(fromShared.selectComplianceUploadPhase)() ===
      'regular',
  );
  public readonly messages = input.required<MessageListingType[]>();
  /**
   * SB-3944 / KD-50: the skill shown's current intro, shown above the stored
   * messages; `null` for a chat with no skill shown (a general chat, a default
   * chat conversation).
   */
  public readonly introMessage = input<string | null>(null);
  protected readonly introMessageId = INTRO_MESSAGE_ID;
  /** SB-3651 — the newest reply waits while its live turn is still on screen. */
  public readonly holdLastReply = input(false);
  /**
   * SB-3978 B5-T21: a saved trace's page row shows its host as text, as the
   * live Teammate turn does (Teammate links inert). Fail-closed: on unless the
   * page says the chat is a general or skill chat.
   */
  public readonly inertTraceLinks = input(true);
  public readonly messagesWithLastMessageFromChatbot = computed(() => {
    const messages = this.messages();
    const lastMessageFromChatbot = messages
      .filter((message) => !message.isFromUser)
      .at(-1);

    return messages.map((message) => ({
      ...message,
      documents: excludeInstructionDocuments(message.documents),
      isLastMessageFromChatbot: message.id === lastMessageFromChatbot?.id,
      thinking: savedThinking(message.thinking),
    }));
  });
  public readonly isLoading = input.required<boolean>();
  public readonly isThinking = input.required<boolean>();
  public readonly isMobile = input.required<boolean>();
  public readonly messageCount = input.required<number>();
  public readonly selectedDocumentInChatMessage =
    input<DocumentInChatMessage | null>(null);
  public readonly documentInChatMessageClicked =
    output<SelectDocumentInChatMessagePayload>();
  // Pure passthrough of the bubble's imageClicked (no `chatMessageId`
  // injection, unlike documentInChatMessageClicked above): the image overlay
  // is keyed only by document — it has no per-message citation state.
  public readonly imageInChatMessageClicked =
    output<ImageInMessageClickedEvent>();
  public readonly newReplyAnimationComplete = output<void>();
  public readonly spacerShouldRecalculate = output<void>();
  public readonly loadingMessage = computed(() => {
    const message = 'Lorem ipsum dolor sit amat consectetur.';
    return this.isMobile() ? message : `${message}\n${message}`;
  });

  private prevIsThinking: boolean | null = null;
  public newReplyMessageId = signal<number | null>(null);

  constructor() {
    effect(() => {
      // Reset the "new reply" indicator when navigating to a different chat
      this.chatUuid();
      this.newReplyMessageId.set(null);
    });

    effect(() => {
      const thinking = this.isThinking();
      const messages = this.messages();
      const lastBotMessage = messages.filter((m) => !m.isFromUser).at(-1);
      const stoppedThinking = !!this.prevIsThinking && !thinking;

      if (thinking) {
        // Clear when the user sends a new message so previous messages don't re-animate
        this.newReplyMessageId.set(null);
      } else if (stoppedThinking && lastBotMessage) {
        // Set the last bot message as a new reply, enables the "new reply" animation
        this.newReplyMessageId.set(lastBotMessage.id);
      }

      this.prevIsThinking = thinking;
    });
  }

  public handleMessageDocumentClicked(
    event: SelectDocumentInChatMessagePayload,
  ) {
    this.documentInChatMessageClicked.emit(event);
  }

  public handleMarkdownAnimationComplete(messageId: number): void {
    if (this.newReplyMessageId() === messageId) {
      this.newReplyAnimationComplete.emit();
    }
  }

  public handleSpacerShouldRecalculate(messageId: number): void {
    if (this.newReplyMessageId() === messageId) {
      this.spacerShouldRecalculate.emit();
    }
  }
}
